/**
 * ORGANIZATION vaults: vault key distribution per member.
 *
 * The team vault key is generated ONCE and distributed by encrypting to
 * ECDH public key of each member (ECIES). Each member opens with private key.
 * Server only guards `sealedVaultKey` (opaque) and cannot open any:
 * it possesses no private key.
 *
 * - Add member: seal vault key to their public key.
 * - Revoke member + ROTATE: generate new key (epoch+1), distribute only to
 *   remaining members, and re-encrypt items. Revoked member, even holding
 *   old `sealedVaultKey`, cannot open new items (new epoch).
 */

import { openWithPrivateKey, type SealedToPublicKey, sealToPublicKey } from "./asymmetric";
import { createVaultKey } from "./vault";
import type { VaultMembership } from "./types";

export interface OrgMemberPublic {
  readonly userId: string;
  readonly publicKey: string;
  readonly role: VaultMembership["role"];
}

function vaultKeyAad(vaultId: string, epoch: number): string {
  return `boveda:org-vault-key:v1:${vaultId}:${epoch}`;
}

export interface CreatedOrgVault {
  readonly vaultId: string;
  readonly keyEpoch: number;
  /** Plaintext vault key — ONLY in creator client. */
  readonly vaultKey: Uint8Array;
  /** Distribution ready to upload (opaque blobs). */
  readonly memberships: VaultMembership[];
}

/** Creates organization vault and distributes its key to given members. */
export async function createOrgVault(
  vaultId: string,
  members: OrgMemberPublic[],
): Promise<CreatedOrgVault> {
  const vaultKey = createVaultKey();
  const keyEpoch = 1;
  const memberships = await distributeVaultKey(vaultId, keyEpoch, vaultKey, members);
  return { vaultId, keyEpoch, vaultKey, memberships };
}

/** Seals vault key to each member (use when creating, adding or rotating). */
export async function distributeVaultKey(
  vaultId: string,
  keyEpoch: number,
  vaultKey: Uint8Array,
  members: OrgMemberPublic[],
): Promise<VaultMembership[]> {
  const aad = vaultKeyAad(vaultId, keyEpoch);
  const out: VaultMembership[] = [];
  for (const m of members) {
    const sealed = await sealToPublicKey(m.publicKey, vaultKey, aad);
    out.push({
      vaultId,
      userId: m.userId,
      role: m.role,
      sealedVaultKey: JSON.stringify(sealed),
      keyEpoch,
    });
  }
  return out;
}

/** A member opens vault key with their private key. */
export async function openMembership(
  privateKeyB64: string,
  membership: VaultMembership,
): Promise<Uint8Array> {
  // Trust boundary: membership written by distribution flow of this app;
  // if blob were corrupted, AEAD open will fail anyway.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const sealed = JSON.parse(membership.sealedVaultKey) as SealedToPublicKey;
  return openWithPrivateKey(
    privateKeyB64,
    sealed,
    vaultKeyAad(membership.vaultId, membership.keyEpoch),
  );
}

export interface RotatedOrgVault {
  readonly keyEpoch: number;
  readonly vaultKey: Uint8Array;
  readonly memberships: VaultMembership[];
}

/**
 * Rotates vault key (e.g. after revoking someone). `remainingMembers`
 * are those retaining access. Returns new key and new distribution;
 * caller must re-encrypt items with `reencryptItem` under new key.
 */
export async function rotateOrgVault(
  vaultId: string,
  previousEpoch: number,
  remainingMembers: OrgMemberPublic[],
): Promise<RotatedOrgVault> {
  const keyEpoch = previousEpoch + 1;
  const vaultKey = createVaultKey();
  const memberships = await distributeVaultKey(vaultId, keyEpoch, vaultKey, remainingMembers);
  return { keyEpoch, vaultKey, memberships };
}
